/* @moldura padrao */
/**
 * form.tsx (custos-pedido) — Lançamento/edição do custo de um pedido.
 *
 * Layout 8/4 (tema AdminUX, base adminux-inventory-order-details): à esquerda o
 * cabeçalho do pedido + a tabela de custos (vai adicionando com o "+"); à direita
 * um painel "sticky" com receita − custos = margem, recalculado ao vivo.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/auth/auth-context';
import { api, ApiError } from '@/lib/api';
import type {
  CategoriaOpcao,
  CustoLinha,
  FornecedorOpcao,
  Loja,
  PedidoDetalhe,
  PedidoIn,
} from './shared';
import {
  SITUACOES,
  codigoDaSlug,
  corGrupo,
  corMargem,
  fmt,
} from './shared';
import { hojeISO } from '@/lib/data';
import { CustomCampoNumeroMoeda } from '@/components/CustomCamposNumero';
import { doBanco, paraBanco, paraConta } from '@/lib/numero';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

const ORIGENS = ['Loja', 'Arquiteto'];

/**
 * A linha COMO A TELA a edita: igual à da API, mas com o valor em string
 * canônica ('' | '80.66'), que é o que o campo de dinheiro fala. A conversão
 * acontece só nas bordas — ao carregar (`doBanco`) e ao salvar (`paraBanco`).
 * `CustoLinha` continua sendo o tipo da API, com `valor: number`.
 */
type LinhaForm = Omit<CustoLinha, 'valor'> & { valor: string };

function linhaVazia(): LinhaForm {
  return {
    categoria_id: null,
    fornecedor_id: null,
    descricao: '',
    num_doc: '',
    valor: '',
    situacao: 'encerrado',
    data: hojeISO(),
  };
}

export function CustosPedidoFormPage() {
  const navigate = useNavigate();
  const { id, loja: slug } = useParams<{ id?: string; loja?: string }>();
  const edicao = Boolean(id);

  const [lojas, setLojas] = useState<Loja[]>([]);
  const [lojaId, setLojaId] = useState<number | null>(null);
  const [catCusto, setCatCusto] = useState<CategoriaOpcao[]>([]);
  const [catVenda, setCatVenda] = useState<CategoriaOpcao[]>([]);
  const [fornecedores, setFornecedores] = useState<FornecedorOpcao[]>([]);

  // Cabeçalho
  const [nrPedido, setNrPedido] = useState('');
  const [cliente, setCliente] = useState('');
  const [categoriaVendaId, setCategoriaVendaId] = useState<string>('');
  const [vendedor, setVendedor] = useState('');
  const [valorVenda, setValorVenda] = useState('');
  const [desconto, setDesconto] = useState('');
  const [formaPgto, setFormaPgto] = useState('');
  const [origem, setOrigem] = useState('Loja');
  const [situacao, setSituacao] = useState('aberto');
  const [data, setData] = useState(hojeISO());
  const [obs, setObs] = useState('');

  // Custos
  const [custos, setCustos] = useState<LinhaForm[]>([linhaVazia()]);
  const { pode } = useAuth();
  // Sem `custos-pedido.margem.ver` o backend devolve as linhas vazias e IGNORA
  // as que vierem no PUT. A tela precisa dizer isso: deixar o botão "Adicionar
  // custo" clicável seria convidar a digitar o que não vai ser gravado.
  const verMargem = pode('custos-pedido.margem.ver');

  const [carregando, setCarregando] = useState(edicao);
  const [salvando, setSalvando] = useState(false);

  // Carrega lojas e (em edição) o pedido
  useEffect(() => {
    api.get<Loja[]>('/lojas').then(setLojas).catch(() => undefined);
  }, []);

  // Resolve loja: no modo novo, pelo slug; em edição, vem do pedido
  useEffect(() => {
    if (edicao || lojas.length === 0) return;
    const codigo = codigoDaSlug(slug);
    setLojaId(lojas.find((l) => l.codigo === codigo)?.id ?? lojas[0]?.id ?? null);
  }, [edicao, slug, lojas]);

  // Opções dependentes da loja
  useEffect(() => {
    if (lojaId == null) return;
    api
      .get<CategoriaOpcao[]>(`/custos-pedido/categorias?tipo=custo&loja_id=${lojaId}`)
      .then(setCatCusto)
      .catch(() => undefined);
    api
      .get<CategoriaOpcao[]>(`/custos-pedido/categorias?tipo=venda&loja_id=${lojaId}`)
      .then(setCatVenda)
      .catch(() => undefined);
  }, [lojaId]);

  useEffect(() => {
    api
      .get<FornecedorOpcao[]>('/custos-pedido/fornecedores')
      .then(setFornecedores)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!edicao) return;
    setCarregando(true);
    api
      .get<PedidoDetalhe>(`/custos-pedido/${id}`)
      .then((p) => {
        setLojaId(p.loja_id);
        setNrPedido(p.nr_pedido);
        setCliente(p.cliente ?? '');
        setCategoriaVendaId(p.categoria_venda_id ? String(p.categoria_venda_id) : '');
        setVendedor(p.vendedor ?? '');
        setValorVenda(doBanco(p.valor_venda, 2, 'pedido.valor_venda'));
        setDesconto(doBanco(p.desconto, 2, 'pedido.desconto'));
        setFormaPgto(p.forma_pgto ?? '');
        setOrigem(p.origem ?? 'Loja');
        setSituacao(p.situacao ?? 'aberto');
        setData(p.data ?? hojeISO());
        setObs(p.obs ?? '');
        setCustos(
          p.custos.length
            ? p.custos.map((c) => ({ ...c, valor: doBanco(c.valor, 2, 'custo.valor') }))
            : [linhaVazia()],
        );
      })
      .catch(() => toast.error('Não foi possível carregar o pedido.'))
      .finally(() => setCarregando(false));
  }, [edicao, id]);

  const grupoDaCategoria = useMemo(() => {
    const m = new Map<number, string>();
    catCusto.forEach((c) => m.set(c.id, c.grupo ?? 'Sem grupo'));
    return m;
  }, [catCusto]);

  const custoTotal = useMemo(
    () => custos.reduce((s, c) => s + paraConta(c.valor), 0),
    [custos],
  );
  const receita = paraConta(valorVenda);
  // EXCEÇÃO à doc 07, e esta é permanente: enquanto se DIGITA não existe
  // servidor para perguntar. É prévia, não verdade — o valor que vale volta do
  // backend em Decimal ao salvar, e é ele que a tela mostra depois.
  const margem = receita - custoTotal;
  const margemPct = receita > 0 ? (margem / receita) * 100 : null;

  const composicao = useMemo(() => {
    const g = new Map<string, number>();
    custos.forEach((c) => {
      const grupo = c.categoria_id ? grupoDaCategoria.get(c.categoria_id) ?? 'Sem grupo' : 'Sem grupo';
      g.set(grupo, (g.get(grupo) ?? 0) + paraConta(c.valor));
    });
    return Array.from(g.entries())
      .map(([grupo, valor]) => ({ grupo, valor, pct: custoTotal > 0 ? (valor / custoTotal) * 100 : 0 }))
      .sort((a, b) => b.valor - a.valor);
  }, [custos, grupoDaCategoria, custoTotal]);

  function setLinha(i: number, patch: Partial<LinhaForm>) {
    setCustos((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }
  function addLinha() {
    setCustos((cs) => [...cs, linhaVazia()]);
  }
  function removerLinha(i: number) {
    setCustos((cs) => (cs.length === 1 ? [linhaVazia()] : cs.filter((_, idx) => idx !== i)));
  }

  async function salvar() {
    if (lojaId == null) return toast.error('Selecione a loja.');
    if (!nrPedido.trim()) return toast.error('Informe o nº do pedido.');
    setSalvando(true);
    const corpo: PedidoIn = {
      loja_id: lojaId,
      nr_pedido: nrPedido.trim(),
      cliente: cliente.trim() || null,
      categoria_venda_id: categoriaVendaId ? Number(categoriaVendaId) : null,
      vendedor: vendedor.trim() || null,
      valor_venda: paraBanco(valorVenda),
      desconto: paraBanco(desconto),
      forma_pgto: formaPgto.trim() || null,
      origem,
      situacao,
      obs: obs.trim() || null,
      data: data || null,
      // `undefined` some do JSON: sem a permissão, o PUT não menciona `custos`,
      // e as linhas gravadas ficam onde estão. O backend também recusa — os dois
      // lados, porque só um deles é o que sobra quando alguém chama a API na mão.
      custos: !verMargem
        ? undefined
        : custos
        .filter((c) => paraConta(c.valor) > 0 || c.categoria_id || c.descricao)
        .map((c) => ({
          categoria_id: c.categoria_id || null,
          fornecedor_id: c.fornecedor_id || null,
          descricao: c.descricao || null,
          num_doc: c.num_doc || null,
          valor: paraBanco(c.valor) ?? 0,
          situacao: c.situacao || 'encerrado',
          data: c.data || null,
        })),
    };
    try {
      const salvo = edicao
        ? await api.put<PedidoDetalhe>(`/custos-pedido/${id}`, corpo)
        : await api.post<PedidoDetalhe>('/custos-pedido', corpo);
      toast.success('Pedido salvo.');
      navigate(`/custos-pedido/${salvo.id}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) {
    return (
      <div className="container py-5 text-center text-secondary">
        <span className="spinner-border spinner-border-sm me-2" /> Carregando…
      </div>
    );
  }

  const lojaNome = lojas.find((l) => l.id === lojaId)?.nome ?? '';

  return (
    <CustomMolduraPagina
      titulo={edicao ? `Editar pedido #${nrPedido}` : 'Novo pedido'}
      descricao={
        lojaNome
          ? `${lojaNome} — lançamento de custos do pedido`
          : 'Lançamento de custos do pedido'
      }
      acoes={
        <>
          <button className="btn btn-link" onClick={() => navigate(-1)}>
            Cancelar
          </button>
          <button className="btn btn-theme" onClick={salvar} disabled={salvando}>
            {salvando && <span className="spinner-border spinner-border-sm me-1" />}
            Salvar
          </button>
        </>
      }
    >

      <div className="row">
        {/* Cabeçalho + custos: largura total */}
        <div className="col-12">
          {/* Cabeçalho do pedido */}
          <div className="card adminuiux-card mb-3 mb-lg-4">
            <div className="card-header bg-transparent">
              <h6 className="mb-0">
                <i className="bi bi-receipt me-2"></i>Dados do pedido
              </h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-6 col-md-3">
                  <label className="form-label small text-secondary">Nº do pedido *</label>
                  <input
                    className="form-control"
                    value={nrPedido}
                    onChange={(e) => setNrPedido(e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label small text-secondary">Data</label>
                  <input
                    type="date"
                    className="form-control"
                    value={data}
                    onChange={(e) => setData(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary">Cliente</label>
                  <input
                    className="form-control"
                    value={cliente}
                    onChange={(e) => setCliente(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary">Canal de venda</label>
                  <select
                    className="form-select"
                    value={categoriaVendaId}
                    onChange={(e) => setCategoriaVendaId(e.target.value)}
                  >
                    <option value="">—</option>
                    {catVenda.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label small text-secondary">Vendedor</label>
                  <input
                    className="form-control"
                    value={vendedor}
                    onChange={(e) => setVendedor(e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label small text-secondary">Origem</label>
                  <select
                    className="form-select"
                    value={origem}
                    onChange={(e) => setOrigem(e.target.value)}
                  >
                    {ORIGENS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label small text-secondary">Valor da venda</label>
                  <CustomCampoNumeroMoeda
                    placeholder="puxar do Tiny"
                    valor={valorVenda}
                    onChange={setValorVenda}
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label small text-secondary">Desconto</label>
                  <CustomCampoNumeroMoeda valor={desconto} onChange={setDesconto} />
                </div>
                <div className="col-6 col-md-4">
                  <label className="form-label small text-secondary">Forma de pagamento</label>
                  <input
                    className="form-control"
                    value={formaPgto}
                    onChange={(e) => setFormaPgto(e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-4">
                  <label className="form-label small text-secondary">Situação</label>
                  <select
                    className="form-select"
                    value={situacao}
                    onChange={(e) => setSituacao(e.target.value)}
                  >
                    {SITUACOES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Custos */}
          <div className="card adminuiux-card mb-3 mb-lg-4">
            <div className="card-header bg-transparent d-flex align-items-center justify-content-between">
              <h6 className="mb-0">
                <i className="bi bi-list-ul me-2"></i>Custos do pedido
              </h6>
              {verMargem && (
                <button className="btn btn-sm btn-theme" onClick={addLinha}>
                  <i className="bi bi-plus-lg me-1"></i>Adicionar custo
                </button>
              )}
            </div>
            <div className="card-body">
              {!verMargem && (
                <div className="alert alert-light border small d-flex gap-2 py-2">
                  <i className="bi bi-eye-slash text-secondary" />
                  <span>
                    Os custos deste pedido estão ocultos: eles dependem do acesso{' '}
                    <strong>Ver custo e margem</strong>. O cabeçalho do pedido
                    você pode editar normalmente.
                  </span>
                </div>
              )}
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0" style={{ fontSize: '0.8rem' }}>
                  <thead>
                    <tr className="text-secondary text-uppercase" style={{ fontSize: '0.68rem' }}>
                      <th style={{ minWidth: 128 }}>Data</th>
                      <th style={{ minWidth: 170 }}>Categoria</th>
                      <th style={{ minWidth: 130 }}>Fornecedor</th>
                      <th style={{ width: '100%', minWidth: 200 }}>Descrição</th>
                      <th style={{ minWidth: 90 }}>Nº doc</th>
                      <th className="text-end" style={{ minWidth: 100 }}>Valor</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {custos.map((c, i) => (
                      <tr key={i}>
                        <td>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={c.data ?? ''}
                            onChange={(e) => setLinha(i, { data: e.target.value })}
                          />
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={c.categoria_id ?? ''}
                            onChange={(e) =>
                              setLinha(i, {
                                categoria_id: e.target.value ? Number(e.target.value) : null,
                              })
                            }
                          >
                            <option value="">—</option>
                            {catCusto.map((cat) => (
                              <option key={cat.id} value={String(cat.id)}>
                                {cat.nome}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={c.fornecedor_id ?? ''}
                            onChange={(e) =>
                              setLinha(i, {
                                fornecedor_id: e.target.value ? Number(e.target.value) : null,
                              })
                            }
                          >
                            <option value="">—</option>
                            {fornecedores.map((f) => (
                              <option key={f.id} value={String(f.id)}>
                                {f.nome}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={c.descricao ?? ''}
                            onChange={(e) => setLinha(i, { descricao: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={c.num_doc ?? ''}
                            onChange={(e) => setLinha(i, { num_doc: e.target.value })}
                          />
                        </td>
                        <td>
                          <CustomCampoNumeroMoeda
                            sm
                            moeda={false}
                            valor={c.valor}
                            onChange={(v) => setLinha(i, { valor: v })}
                          />
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-link btn-square text-danger p-0"
                            onClick={() => removerLinha(i)}
                            title="Remover"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="fw-semibold border-top">
                      <td colSpan={5} className="text-end">
                        Total dos custos
                      </td>
                      <td className="text-end">{fmt(custoTotal)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Resumo ao vivo (margem): largura total, abaixo dos custos */}
        <div className="col-12">
          <div className="card adminuiux-card mb-3 mb-lg-4">
            <div className="card-body">
              <p className="text-secondary small mb-1">Margem do pedido</p>
              <h3 className={`mb-1 ${corMargem(margemPct)}`}>
                {margemPct != null ? `${margemPct.toFixed(0)}%` : '—'}
              </h3>
              <p className="text-secondary small mb-3">
                {margemPct != null ? fmt(margem) : 'informe o valor da venda'}
              </p>

              <div className="row gx-3 mb-2">
                <div className="col text-secondary">Receita</div>
                <div className="col-auto">{valorVenda !== '' ? fmt(receita) : '—'}</div>
              </div>
              <div className="row gx-3 mb-2">
                <div className="col text-secondary">(−) Custos</div>
                <div className="col-auto text-danger">{fmt(custoTotal)}</div>
              </div>
              <hr className="my-2" />
              <div className="row gx-3 mb-3 fw-semibold">
                <div className="col">(=) Margem</div>
                <div className={`col-auto ${corMargem(margemPct)}`}>{fmt(margem)}</div>
              </div>

              {composicao.length > 0 && custoTotal > 0 && (
                <>
                  <p className="text-secondary small mb-2">Composição do custo</p>
                  <div className="progress mb-2" style={{ height: 8 }}>
                    {composicao.map((g) => (
                      <div
                        key={g.grupo}
                        className="progress-bar"
                        style={{ width: `${g.pct}%`, backgroundColor: corGrupo(g.grupo) }}
                        title={`${g.grupo}: ${g.pct.toFixed(0)}%`}
                      />
                    ))}
                  </div>
                  {composicao.map((g) => (
                    <div key={g.grupo} className="row gx-2 align-items-center small mb-1">
                      <div className="col-auto">
                        <span
                          className="d-inline-block rounded-circle"
                          style={{ width: 10, height: 10, backgroundColor: corGrupo(g.grupo) }}
                        />
                      </div>
                      <div className="col text-secondary text-truncate">{g.grupo}</div>
                      <div className="col-auto">{fmt(g.valor)}</div>
                      <div className="col-auto text-secondary" style={{ width: 42, textAlign: 'right' }}>
                        {g.pct.toFixed(0)}%
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </CustomMolduraPagina>
  );
}
